import { useEffect, useRef, useState } from 'react';
import { api, type Run } from '../api/client';

const TERMINAL = new Set(['SUCCESS', 'FAILED']);

/** Опрашивает прогон, пока он не завершён. */
export function useRunPolling(runId: string | undefined, initial: Run | null) {
  const [run, setRun] = useState<Run | null>(initial);
  const terminalRef = useRef(false);

  useEffect(() => {
    setRun(initial);
    terminalRef.current = initial ? TERMINAL.has(initial.status) : false;
  }, [initial]);

  useEffect(() => {
    if (!runId) return;

    let cancelled = false;

    const poll = async () => {
      const fresh = await api.get<Run>(`/api/runs/${runId}`);
      if (cancelled) return;
      setRun(fresh);
      terminalRef.current = TERMINAL.has(fresh.status);
    };

    void poll();
    const id = window.setInterval(() => {
      if (!terminalRef.current) {
        void poll().catch(() => undefined);
      }
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runId]);

  return run;
}

export type RunProgress = {
  current_stage?: string;
  current_detail?: string;
  last_heartbeat_at?: string;
  stages?: Array<{
    name: string;
    status: string;
    error?: string | null;
    duration_sec?: number | null;
    items_in?: number | null;
    items_out?: number | null;
    details?: Record<string, unknown> | null;
  }>;
};

export function getRunProgress(run: Run | null): RunProgress {
  const stats = run?.stats as RunProgress | null | undefined;
  return stats ?? {};
}

export const STAGE_LABELS: Record<string, string> = {
  starting: 'Запуск',
  quota_check: 'Проверка квот',
  ingest: 'Загрузка данных',
  ingest_done: 'Данные загружены',
  analytics: 'Аналитика событий',
  analytics_done: 'Аналитика завершена',
  bhapi: 'Детализация карточек',
  bhapi_done: 'Детализация завершена',
  llm_events: 'AI-комментарии к карточкам',
  llm_sections: 'AI-сводки разделов 4–7',
  llm_done: 'AI-обработка завершена',
  notify: 'Уведомления',
  success: 'Готово',
  failed: 'Ошибка',
};

export const STAGE_HINTS: Record<string, { what: string; why: string }> = {
  quota_check: {
    what: 'Проверяет остаток лимитов перед массовыми запросами.',
    why: 'Помогает понять, хватит ли внешней квоты и в каком состоянии был API на старте.',
  },
  ingest: {
    what: 'Загружает карточки, бренды, продавцов и прогнозы по выбранным категориям.',
    why: 'Это сырьё для аналитики и отчёта; большинство ошибок источников видно здесь.',
  },
  analytics: {
    what: 'Применяет формальные правила NEW-WINNER и OLD-CARD-HYPER-GROWTH.',
    why: 'AI не ищет тренды, поэтому именно эта стадия определяет события отчёта.',
  },
  bhapi: {
    what: 'Дополняет TOP-N карточек подробными данными.',
    why: 'Даёт URL, описание и контентные признаки, но не должен валить весь прогон.',
  },
  llm_events: {
    what: 'Готовит AI-комментарии к найденным событиям.',
    why: 'Превращает числовой сигнал в понятное объяснение для отчёта.',
  },
  llm_sections: {
    what: 'Формирует аналитические разделы 4–7.',
    why: 'Даёт паттерны, гипотезы и рекомендации по итогам прогона.',
  },
  notify: {
    what: 'Отправляет Email и Telegram уведомления.',
    why: 'Подтверждает завершение прогона и даёт ссылку на отчёт.',
  },
};
